import React, { useEffect } from 'react';
import { 
  Sparkles, CheckCircle2, Lock, ArrowRight, UserPlus, 
  ShieldCheck, Zap, BookOpen, Flame, HelpCircle 
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { StorageService } from '../services/storageService';

interface GuestLimitModalProps {
  isOpen: boolean;
  onClose?: () => void;
  limit: number;
  totalExplored: number;
  featureName?: string;
  onSignUp?: () => void;
}

export const GuestLimitModal: React.FC<GuestLimitModalProps> = ({
  isOpen,
  onClose,
  limit,
  totalExplored,
  featureName = "Practice & Flashcards",
  onSignUp
}) => {
  const tokens = useThemeTokens();

  useEffect(() => {
    if (isOpen) {
      StorageService.trackUserEvent({
        action: 'guest_limit_modal_shown',
        category: 'guest_trial',
        label: `Guest Limit Encounter (${totalExplored}/${limit})`,
        value: totalExplored,
        metadata: {
          limit,
          totalExplored,
          featureName
        }
      });
    }
  }, [isOpen, limit, totalExplored, featureName]);

  if (!isOpen) return null;

  const handleSignUpClick = () => {
    StorageService.trackUserEvent({
      action: 'guest_signup_cta_clicked',
      category: 'conversion',
      label: 'Guest Limit Modal - Create Free Account Clicked',
      metadata: {
        limit,
        totalExplored,
        featureName
      }
    });

    if (onSignUp) {
      onSignUp();
    } else {
      const currentFullUrl = window.location.href;
      const registerUrl = `https://www.diamondnursingschool.com/studyhub-login?mode=register&redirect=${encodeURIComponent(currentFullUrl)}`;
      window.location.href = registerUrl;
    }
  };

  const handleLoginClick = () => {
    StorageService.trackUserEvent({
      action: 'guest_login_cta_clicked',
      category: 'conversion',
      label: 'Guest Limit Modal - Sign In Clicked',
      metadata: {
        limit,
        totalExplored,
        featureName
      }
    });

    const currentFullUrl = window.location.href;
    const loginUrl = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentFullUrl)}`;
    window.location.href = loginUrl;
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl p-6 sm:p-8 space-y-6 text-left my-auto animate-scale-up">
        {/* Header Icon & Tag */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3.5 bg-gradient-to-tr from-amber-500 to-orange-500 text-white rounded-2xl shadow-lg">
              <Sparkles size={26} className="animate-pulse" />
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded-full text-[10px] font-black tracking-widest uppercase border border-amber-200 dark:border-amber-900/50">
                Free Trial Limit Reached ({totalExplored}/{limit})
              </span>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-1">
                You explored {limit} items!
              </h3>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <p className="text-slate-600 dark:text-slate-300 text-sm font-medium leading-relaxed">
          You've reached the free <strong className="text-slate-900 dark:text-white font-black">{limit}-item exploration limit</strong> for guest mode. Create your free account to unlock continuous practice, save your progress, track mastery, and access the full clinical question bank.
        </p>

        {/* Feature Unlocks List */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-2.5">
          <div className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
            Create your free account to unlock:
          </div>
          <div className="flex items-center gap-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
            <span>Unlimited access to 1,000+ certified exam questions</span>
          </div>
          <div className="flex items-center gap-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
            <span>Spaced-repetition flashcards with mastery tracking</span>
          </div>
          <div className="flex items-center gap-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
            <span>Personalized Mistake Vault & AI clinical rationales</span>
          </div>
          <div className="flex items-center gap-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
            <span>State board readiness score and progress analytics</span>
          </div>
        </div>

        {/* Call to Actions */}
        <div className="space-y-3 pt-2">
          <button
            onClick={handleSignUpClick}
            className={`w-full py-4.5 rounded-2xl font-black text-sm uppercase tracking-wider text-white shadow-xl transition-all active:scale-98 flex items-center justify-center gap-3 ${tokens.primaryBtn}`}
          >
            <UserPlus size={18} />
            <span>Create Free Account to Continue</span>
            <ArrowRight size={16} />
          </button>

          <div className="flex items-center justify-between text-xs font-bold pt-1">
            <span className="text-slate-400">Already have an account?</span>
            <button
              onClick={handleLoginClick}
              className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-black"
            >
              Sign In Here <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
